<template>
  <div class="theme-switcher">
    <div class="theme-switcher__label">Theme</div>
    <div class="theme-switcher__toggle" role="radiogroup" aria-label="Theme preference">
      <button
        type="button"
        role="radio"
        :aria-checked="currentTheme === 'light'"
        :class="['theme-switcher__option', { 'theme-switcher__option--active': currentTheme === 'light' }]"
        @click="setTheme('light')"
      >
        <LucideIcon name="Sun" :size="14" />
        <span>Light</span>
      </button>
      <button
        type="button"
        role="radio"
        :aria-checked="currentTheme === 'dark'"
        :class="['theme-switcher__option', { 'theme-switcher__option--active': currentTheme === 'dark' }]"
        @click="setTheme('dark')"
      >
        <LucideIcon name="Moon" :size="14" />
        <span>Dark</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import LucideIcon from '../LucideIcon/LucideIcon.vue';

const STORAGE_KEY = 'cassie-theme-preference';

export default Vue.extend({
  name: 'ThemeSwitcher',

  components: {
    LucideIcon
  },

  data() {
    return {
      currentTheme: 'light' as 'light' | 'dark'
    };
  },

  mounted() {
    this.loadThemePreference();
  },

  methods: {
    loadThemePreference() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'dark' || saved === 'light') {
          this.currentTheme = saved;
        } else {
          this.currentTheme = 'light'; // Default to light
        }
        this.applyTheme(this.currentTheme);
      } catch (e) {
        // localStorage not available, use default
        this.currentTheme = 'light';
        this.applyTheme(this.currentTheme);
      }
    },

    setTheme(theme: 'light' | 'dark') {
      if (this.currentTheme === theme) return;

      this.currentTheme = theme;
      this.applyTheme(theme);
      this.saveThemePreference(theme);
      this.$emit('theme-change', theme);
    },

    applyTheme(theme: 'light' | 'dark') {
      const root = document.documentElement;
      if (theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      // Also toggle Vuetify's dark mode for full component support
      if (this.$vuetify) {
        this.$vuetify.theme.dark = theme === 'dark';
      }
    },

    saveThemePreference(theme: 'light' | 'dark') {
      try {
        localStorage.setItem(STORAGE_KEY, theme);
      } catch (e) {
        // localStorage not available, ignore
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.theme-switcher {
  padding: 12px 16px;

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
    margin-bottom: 8px;
  }

  &__toggle {
    display: flex;
    gap: 4px;
    background: #f3f4f6;
    background: var(--muted, #f3f4f6);
    border-radius: var(--radius);
    padding: 4px;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    padding: 6px 12px;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 150ms ease;

    &:hover:not(&--active) {
      background: rgba(0, 0, 0, 0.05);
    }

    &:focus-visible {
      outline: 2px solid #406A94;
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 2px;
    }

    &--active {
      background: #ffffff;
      background: var(--card, #ffffff);
      color: #111827;
      color: var(--foreground, #111827);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
  }
}

// Dark theme adjustments
:global(.dark) .theme-switcher {
  &__toggle {
    background: #374151;
  }

  &__option {
    color: #9ca3af;

    &:hover:not(.theme-switcher__option--active) {
      background: rgba(255, 255, 255, 0.05);
    }

    &--active {
      background: #1f2937;
      color: #f9fafb;
    }
  }
}
</style>
